"use client"

import type React from "react"
import { useState, useEffect, useRef } from "react"
import {
  FaSearch,
  FaEdit,
  FaMapMarkerAlt,
  FaProjectDiagram,
  FaFileExcel,
  FaFilter,
  FaUserCheck,
  FaFileAlt,
  FaCheckCircle,
  FaPause,
  FaPlay,
  FaTimes,
  FaTasks,
  FaUser,
  FaUserPlus,
  FaClock,
  FaTimesCircle,
  FaPlus,
  FaHourglass,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaPaperPlane,
  FaBan,
} from "react-icons/fa"
import {
  FiSearch,
  FiFilter,
  FiX,
  FiMapPin,
  FiCalendar,
  FiUser,
  FiRefreshCw,
  FiSettings,
  FiEye,
  FiEyeOff
} from 'react-icons/fi';
import { Pagination as Pagination } from "../../utils/pagination.utils"
import { ExcelDownload } from "../../utils/excel.utils"
import { useAuth } from "../../provider/auth.provider"
import { LuEye } from "react-icons/lu"
import { Link } from "react-router-dom"
import { SearchableSelect, SelectWithSearch, SelectWithSearch2, type SelectOption } from "../../utils/select-with-search"
import api from "../../libs/axios"
import CancelHoldModal from "./view/cancel_hold.modal"
import { useDebouncedCallback } from "../../hooks/useDebounce"
import toast from "react-hot-toast"
import { useConfirm } from "../../provider/confirm.provider"
import { dateRangeInputBounds, firstDateRangeError, type DateRangeTriple } from "../../utils/date-range.utils"

interface Client {
  id: number
  client_name: string
  client_short_name?: string
  status: string
}

const STAGE_GROUP_MAP: Record<string, string> = {
  survey_pending: ["site_created", "engineer_assigned", "field_work_in_progress", "data_uploaded"].join(","),
  field_data_upload_pending: ["field_work_in_progress", "data_uploaded"].join(","),
  survey_done: [
    "field_work_completed", "ta_review_pending", "ta_review_completed", "ta_rejected",
    "mis_report_assign_pending", "report_maker_assigned", "report_in_progress",
    "report_completed", "report_maker_rejected", "validator_assigned",
    "validation_in_progress", "validation_completed", "validation_rejected",
    "report_finalized", "report_sent_to_client"
  ].join(","),
  field_work_approved: [
    "ta_review_completed",
    "mis_report_assign_pending", "report_maker_assigned", "report_in_progress",
    "report_completed", "report_maker_rejected", "validator_assigned",
    "validation_in_progress", "validation_completed", "validation_rejected",
    "report_finalized", "report_sent_to_client"
  ].join(","),
  report_completed: [
    "report_completed", "report_maker_rejected", "validator_assigned",
    "validation_in_progress", "validation_completed", "validation_rejected",
    "report_finalized", "report_sent_to_client"
  ].join(","),
  report_finalized: ["report_finalized", "report_sent_to_client"].join(","),
  report_preparation_pending: ["field_work_completed", "ta_review_completed", "report_maker_assigned"].join(","),
  validation_pending: ["validator_assigned", "report_completed"].join(","),
  report_submission_pending: [
    "field_work_completed", "ta_review_completed", "report_maker_assigned",
    "report_completed", "validator_assigned", "report_finalized"
  ].join(","),
}

const STAGE_GROUP_OPTIONS = [
  { value: "survey_pending", label: "Survey Pending" },
  { value: "field_data_upload_pending", label: "Field Data Upload Pending" },
  { value: "survey_done", label: "Survey Done" },
  { value: "field_work_approved", label: "Field Work Approved" },
  { value: "report_completed", label: "Report Completed" },
  { value: "report_finalized", label: "Report Finalized" },
  { value: "report_preparation_pending", label: "Report Preparation Pending" },
  { value: "validation_pending", label: "Validation Pending" },
  { value: "report_submission_pending", label: "Report Submission Pending" },
]

const WORKFLOW_STAGE_FILTERS: SelectOption[] = [
  { id: 1, label: "Site Created", value: "site_created" },
  { id: 2, label: "Engineer Assigned", value: "engineer_assigned" },
  { id: 3, label: "Field Work In Progress", value: "field_work_in_progress" },
  { id: 4, label: "Field Work Completed", value: "field_work_completed" },
  { id: 5, label: "Field Data Upload Pending", value: "field_data_upload_pending" },
  { id: 6, label: "Site Data WIP", value: "data_uploaded" },
  { id: 7, label: "TA Review Pending", value: "ta_review_pending_filter" },
  { id: 8, label: "TA Review Completed", value: "ta_review_completed" },
  { id: 9, label: "MIS Report Assign Pending", value: "mis_report_assign_pending_filter" },
  { id: 10, label: "Report Maker Assigned", value: "report_maker_assigned" },
  { id: 11, label: "Report In Progress", value: "report_in_progress_filter" },
  { id: 12, label: "Report Completed", value: "report_completed" },
  { id: 13, label: "Validator Assigned", value: "validator_assigned" },
  { id: 14, label: "Validation Pending", value: "validation_pending_filter" },
  { id: 15, label: "Report Finalized", value: "report_finalized" },
  { id: 16, label: "Report Sent To Client", value: "report_sent_to_client" },
]

const WORKFLOW_STAGE_FILTER_MAP: Record<string, string> = {
  field_data_upload_pending: ["field_work_in_progress", "data_uploaded"].join(","),
  ta_review_pending_filter: "field_work_completed",
  mis_report_assign_pending_filter: "ta_review_completed",
  report_in_progress_filter: "report_maker_assigned",
  validation_pending_filter: "validator_assigned",
}

const WORKFLOW_STATUS_OPTIONS: SelectOption[] = [
  { id: 1, label: "Site Created", value: "site_created" },
  { id: 2, label: "Engineer Assigned", value: "engineer_assigned" },
  { id: 3, label: "Field Work In Progress", value: "field_work_in_progress" },
  { id: 4, label: "Field Work Completed", value: "field_work_completed" },
  { id: 5, label: "Site Data WIP", value: "data_uploaded" },
  { id: 6, label: "TA Review Pending", value: "ta_review_pending" },
  { id: 7, label: "TA Review Completed", value: "ta_review_completed" },
  { id: 8, label: "MIS Report Assign Pending", value: "mis_report_assign_pending" },
  { id: 9, label: "Report Maker Assigned", value: "report_maker_assigned" },
  { id: 10, label: "Report In Progress", value: "report_in_progress" },
  { id: 11, label: "Report Completed", value: "report_completed" },
  { id: 12, label: "Validator Assigned", value: "validator_assigned" },
  { id: 13, label: "Validation Completed", value: "validation_completed" },
  { id: 14, label: "Report Finalized", value: "report_finalized" },
  { id: 15, label: "Report Sent To Client", value: "report_sent_to_client" },
]

/**
 * EVERY survey workflow stage, for the manual stage override.
 *
 * Deliberately longer than WORKFLOW_STATUS_OPTIONS above: that list is for
 * filtering, so it skips the transient and rejection stages nobody filters by.
 * The override has to be able to reach any stage, including those.
 */
const ALL_WORKFLOW_STAGE_OPTIONS: { value: string; label: string }[] = [
  { value: "site_created", label: "Site Created" },
  { value: "engineer_assigned", label: "Engineer Assigned" },
  { value: "field_work_in_progress", label: "Field Work In Progress" },
  { value: "data_uploaded", label: "Site Data WIP" },
  { value: "field_work_completed", label: "Field Work Completed" },
  { value: "ta_review_pending", label: "TA Review Pending" },
  { value: "ta_review_completed", label: "TA Review Completed" },
  { value: "ta_rejected", label: "TA Rejected" },
  { value: "mis_report_assign_pending", label: "MIS Report Assign Pending" },
  { value: "report_maker_assigned", label: "Report Maker Assigned" },
  { value: "report_in_progress", label: "Report In Progress" },
  { value: "report_completed", label: "Report Completed" },
  { value: "report_maker_rejected", label: "Report Maker Rejected" },
  { value: "validator_assigned", label: "Validator Assigned" },
  { value: "validation_in_progress", label: "Validation In Progress" },
  { value: "validation_completed", label: "Validation Completed" },
  { value: "validation_rejected", label: "Validation Rejected" },
  { value: "report_finalized", label: "Report Finalized" },
  { value: "report_sent_to_client", label: "Report Sent To Client" },
]

const SITE_ENGINEER_ASSIGNABLE_ROLE_IDS = [7, 4]

interface Activity {
  id: number
  activity_name: string
  status: string
}

interface Project {
  id: number
  project_name: string
  project_short_name?: string
  status: string
  client?: Client
  activity?: Activity
}

interface User {
  id: number
  first_name: string
  last_name: string
  username?: string
  email: string
  role_id: number
}

interface Site {
  id: number
  site_id: string
  project_id: number
  status: string
  site_priority?: string
  site_unlock_time?: string | null
  field_work_started_date?: string | null
  field_work_completed_date?: string | null
  first_data_upload_date?: string | null
  created_at: string
  updated_at: string
  created_by: number
  assigned_engineer_id?: number
  assigned_ta_id?: number
  assigned_report_maker_id?: number
  assigned_validator_id?: number
  visit_remarks?: string
  cancel_reason?: string
  hold_reason?: string
  project?: Project
  activity?: Activity
  site_engineer?: User
  ta?: User
  mis?: User
  mis_userid?: number | null
  mis_username?: string | null
  mis_name?: string | null
  report_designer?: User
  validator?: User
  team_coordinator?: User
  survey_workflow_status: string
  has_pending_billing?: boolean
  site_engineer_assign_date?: string | null
  ta_work_date?: string | null
  report_designer_work_date?: string | null
  validator_work_date?: string | null
  report_finalized_date?: string | null
  report_sent_to_client_date?: string | null
  // Additional fields
  reference_number?: string
  client_allocation_date?: string | null
  site_name?: string
  site_address?: string
  opco_name?: string
  opco_id?: string | number
  state_circle?: { state_name: string }
  district?: { name: string }
  pincode?: string | number
  site_latitude?: string | number
  site_longitude?: string | number
  technician_name?: string
  technician_number?: string
  fse_name?: string
  fse_number?: string
  aom_name?: string
  aom_number?: string
  customer_requirement?: string
  site_information1?: string
  site_information2?: string
  site_information3?: string
  creator?: User
  is_location_verified?: boolean
  location_verification_attempts?: number
  source?: string
  workflow_status_dates?: { report_completed?: string }
}

const getUserDisplayName = (user?: User | null): string => {
  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim()
  return fullName || ""
}

const getMisDisplayName = (site: Site): string => {
  return getUserDisplayName(site.mis) || site.mis_name || site.mis_username || ""
}

export interface Filters {
  // Basic Site Information
  site_id?: string;
  site_name?: string;
  site_address?: string;
  activity_id?: string;
  reference_number?: string;
  project_id?: string;
  project_name?: string;
  site_engineer_name?: string;

  // Location and Organization
  state_circle_id?: string;
  district_id?: string;
  pincode?: string;
  opco_name?: string;
  opco_id?: string;

  // Site Priority and Status
  site_priority?: string;
  status?: string;
  survey_workflow_status?: string;

  // Date Range Filters
  created_from?: string;
  created_to?: string;
  field_work_started_from?: string;
  field_work_started_to?: string;
  field_work_completed_from?: string;
  field_work_completed_to?: string;
  ta_review_from?: string;
  ta_review_to?: string;
  report_completed_from?: string;
  report_completed_to?: string;
  report_finalized_from?: string;
  report_finalized_to?: string;
  sent_to_client_from?: string;
  sent_to_client_to?: string;

  // PO/PR Information
  pr_number?: string;
  po_number?: string;
  is_po_no_shared_at_allocation?: string;

  // Contact Information
  contact_person?: string;
  contact_number?: string;

  // Technician Information
  technician_name?: string;
  technician_number?: string;
  fse_name?: string;
  fse_number?: string;
  aom_name?: string;
  aom_number?: string;

  // Client Information
  client_project_name?: string;
  customer_requirement?: string;

  // Assignment Status Filters
  pending_assignment?: string;
  has_assigned_mis?: string;
  has_assigned_engineer?: string;
  has_assigned_ta?: string;
  has_assigned_report_maker?: string;
  has_assigned_validator?: string;
  blank_filter?: string;
  site_engineer_userid?: string;
  ta_userid?: string;
  mis_userid?: string;
  report_designer_userid?: string;
  validator_userid?: string;
  report_maker_name?: string;

  // Multiple Status Filter
  status_list?: string;
  workflow_status_list?: string;
  priority_list?: string;
  stage_group?: string;
  stage_filter?: string;
  // "My Pending Work" chip — server narrows to sites waiting on the logged-in user
  my_pending_stage?: string;
}

// Every from/to pair on the Dates tab, with the heading it is shown under.
const DATE_RANGE_FILTERS: Array<[keyof Filters, keyof Filters, string]> = [
  ['created_from', 'created_to', 'Site Created Date'],
  ['field_work_started_from', 'field_work_started_to', 'Field Work Started Date'],
  ['field_work_completed_from', 'field_work_completed_to', 'Field Work Completed Date'],
  ['ta_review_from', 'ta_review_to', 'TA Review Date'],
  ['report_completed_from', 'report_completed_to', 'Report Completed Date'],
  ['report_finalized_from', 'report_finalized_to', 'Report Finalised Date'],
  ['sent_to_client_from', 'sent_to_client_to', 'Send to Client Date'],
];

// Why these filters cannot be applied (a from/to pair too far apart), or null.
const filtersDateRangeError = (filters: Filters): string | null =>
  firstDateRangeError(
    DATE_RANGE_FILTERS.map(([fromKey, toKey, label]): DateRangeTriple => [filters[fromKey], filters[toKey], label])
  );

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  filters: Filters;
  onFiltersChange: (filters: Filters) => void;
  onApply: () => void;
  onReset: () => void;
  projects?: Project[];
  activities?: Activity[];
  siteEngineerOptions?: SelectOption[];
  taOptions?: SelectOption[];
  misOptions?: SelectOption[];
  reportMakerOptions?: SelectOption[];
  validatorOptions?: SelectOption[];
  onUserSearchChange?: (term: string) => void;
  usersLoading?: boolean;
}

const FilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  onClose,
  filters,
  onFiltersChange,
  onApply,
  onReset,
  projects = [],
  activities = [],
  siteEngineerOptions = [],
  taOptions = [],
  misOptions = [],
  reportMakerOptions = [],
  validatorOptions = [],
  onUserSearchChange,
  usersLoading = false,
}) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [selectedSiteId, setSelectedSiteId] = useState<SelectOption | null>(null);
  const [selectedStateCircle, setSelectedStateCircle] = useState<SelectOption | null>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<SelectOption | null>(null);
  const [selectedActivity, setSelectedActivity] = useState<SelectOption | null>(null);
  const [selectedWorkflowStatus, setSelectedWorkflowStatus] = useState<SelectOption | null>(null);

  useEffect(() => {
    if (!filters.site_id) setSelectedSiteId(null);
    if (!filters.state_circle_id) setSelectedStateCircle(null);
    if (!filters.district_id) setSelectedDistrict(null);
    if (!filters.activity_id) setSelectedActivity(null);
    if (!filters.survey_workflow_status && !filters.workflow_status_list && !filters.stage_filter) setSelectedWorkflowStatus(null);
  }, [filters]);

  if (!isOpen) return null;

  const handleInputChange = (field: keyof Filters, value: string) => {
    onFiltersChange({
      ...filters,
      [field]: value || undefined
    });
  };

  const handleSelectChange = (field: keyof Filters, option: SelectOption | null) => {
    onFiltersChange({
      ...filters,
      [field]: option ? option.value.toString() : undefined
    });
  };

  const projectOptions = projects.map(p => ({
    value: p.id,
    label: `${p.project_name} (${p.id})`,
  }));

  const selectedProjectOptions = projectOptions.filter((option) =>
    String(filters.project_id || "")
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean)
      .includes(String(option.value))
  );

  const handleProjectMultiChange = (options: Array<{ value: string | number; label: string }> = []) => {
    onFiltersChange({
      ...filters,
      project_id: options.length ? options.map((option) => option.value).join(",") : undefined,
    });
  };

  const renderProjectMultiSelect = () => (
    <SearchableSelect
      value={selectedProjectOptions}
      onChange={handleProjectMultiChange}
      options={projectOptions}
      placeholder="Search projects (min 3 chars)"
      isMulti
      showSelectAllVisible
      minSearchLength={3}
    />
  );

  const handleWorkflowStageChange = (value: string) => {
    const option = WORKFLOW_STAGE_FILTERS.find((item) => item.value.toString() === value) || null;
    setSelectedWorkflowStatus(option);
    const mappedStatuses = value ? WORKFLOW_STAGE_FILTER_MAP[value] : undefined;

    onFiltersChange({
      ...filters,
      stage_filter: value || undefined,
      survey_workflow_status: mappedStatuses ? undefined : value || undefined,
      workflow_status_list: mappedStatuses,
      stage_group: undefined,
      my_pending_stage: undefined,
    });
  };

  const handleStateCircleSelect = (option: SelectOption | null) => {
    setSelectedStateCircle(option);
    setSelectedDistrict(null);

    onFiltersChange({
      ...filters,
      state_circle_id: option ? option.value.toString() : undefined,
      district_id: undefined,
    });
  };

  const renderDateRangeSection = (title: string, fromKey: keyof Filters, toKey: keyof Filters) => {
    const bounds = dateRangeInputBounds(filters[fromKey], filters[toKey]);
    return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900">{title}</h3>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">From</label>
          <input
            type="date"
            className="w-full px-3 py-2.5 border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            value={(filters[fromKey] as string) || ''}
            onChange={(e) => handleInputChange(fromKey, e.target.value)}
            {...bounds.from}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">To</label>
          <input
            type="date"
            className="w-full px-3 py-2.5 border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            value={(filters[toKey] as string) || ''}
            onChange={(e) => handleInputChange(toKey, e.target.value)}
            {...bounds.to}
          />
        </div>
      </div>
    </div>
    );
  };

  const tabs = [
    { id: 'basic', label: 'Site Info', icon: FiSearch },
    { id: 'location', label: 'Location', icon: FiMapPin },
    { id: 'dates', label: 'Dates', icon: FiCalendar },
    { id: 'assignments', label: 'Assignments', icon: FiUser },
    { id: 'status', label: 'Stages', icon: FiSettings }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded shadow-2xl max-w-5xl w-full max-h-[90vh] min-h-[520px] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-800 to-blue-900">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <FiFilter className="w-5 h-5" />
              Filters
            </h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <FiX className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex bg-gray-50 border-b border-gray-200 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 text-sm font-medium whitespace-nowrap flex items-center gap-2 border-b-2 transition-colors ${activeTab === tab.id
                ? 'border-blue-500 text-blue-600 bg-white'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div
          className="p-6 overflow-y-auto flex-1"
          onPointerDown={(event) => {
            if (event.target === event.currentTarget) {
              event.stopPropagation()
            }
          }}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              event.stopPropagation()
            }
          }}
        >
          {/* Site Info Tab */}
          {activeTab === 'basic' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Project Name (Search)</label>
                  {renderProjectMultiSelect()}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Site ID</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter Site ID"
                    value={filters.site_id || ''}
                    onChange={(e) => handleInputChange('site_id', e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Site Name</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter Site Name"
                    value={filters.site_name || ''}
                    onChange={(e) => handleInputChange('site_name', e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Activity (Search)</label>
                  <SelectWithSearch2
                    placeholder="Search activity (min 3 chars)"
                    value={selectedActivity}
                    onChange={(option) => {
                      setSelectedActivity(option);
                      handleSelectChange('activity_id', option);
                    }}
                    options={activities.map(a => ({
                      id: a.id,
                      label: `${a.activity_name} (${a.id})`,
                      value: a.id
                    }))}
                    className="w-full"
                    minSearchLength={3}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Reference Number</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter Reference Number"
                    value={filters.reference_number || ''}
                    onChange={(e) => handleInputChange('reference_number', e.target.value)}
                  />
                </div>
              </div>

              {/* PR/PO Details */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">PR/PO Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">PR Number</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2.5 border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="PR Number"
                      value={filters.pr_number || ''}
                      onChange={(e) => handleInputChange('pr_number', e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">PO Number</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2.5 border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="PO Number"
                      value={filters.po_number || ''}
                      onChange={(e) => handleInputChange('po_number', e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">PO Shared at Allocation</label>
                    <select
                      className="w-full px-3 py-2.5 border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      value={filters.is_po_no_shared_at_allocation || ''}
                      onChange={(e) => handleInputChange('is_po_no_shared_at_allocation', e.target.value)}
                    >
                      <option value="">All</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Location Tab */}
          {activeTab === 'location' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Project Name (Search)</label>
                {renderProjectMultiSelect()}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">State Circles</label>
                <SelectWithSearch
                  placeholder="Select State Circle"
                  value={selectedStateCircle}
                  onChange={handleStateCircleSelect}
                  apiEndpoint="/state-circle/list"
                  dataKey="states"
                  labelKey="state_name"
                  valueKey="id"
                  searchParam="search"
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Districts</label>
                <SelectWithSearch
                  placeholder="Select District"
                  value={selectedDistrict}
                  onChange={(option) => {
                    setSelectedDistrict(option);
                    handleSelectChange('district_id', option);
                  }}
                  apiEndpoint="/state-circle"
                  dataKey="districts"
                  labelKey="name"
                  valueKey="id"
                  searchParam="search"
                  dependencyId={selectedStateCircle ? Number(selectedStateCircle.value) : null}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Pincode</label>
                <input
                  type="number"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Pincode"
                  value={filters.pincode || ''}
                  onChange={(e) => handleInputChange('pincode', e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">OPCO Name</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="OPCO Name"
                  value={filters.opco_name || ''}
                  onChange={(e) => handleInputChange('opco_name', e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">OPCO ID</label>
                {/* <SelectWithSearch2
                  placeholder="Select OPCO"
                  value={selectedOpco}
                  onChange={(option) => {
                    setSelectedOpco(option);
                    handleSelectChange('opco_id', option);
                  }}
                  apiEndpoint="/sites/opcos"
                  dataKey="states"
                  labelKey="opco_name"
                  valueKey="id"
                  searchParam="search"
                  className="w-full"
                /> */}
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Enter OPCO ID"
                  value={filters.opco_id || ''}
                  onChange={(e) => handleInputChange('opco_id', e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Dates Tab */}
          {activeTab === 'dates' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2 lg:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Project Name (Search)</label>
                <div className="max-w-sm">
                  {renderProjectMultiSelect()}
                </div>
              </div>
              {renderDateRangeSection('Site Created Date', 'created_from', 'created_to')}
              {renderDateRangeSection('Field Work Started Date', 'field_work_started_from', 'field_work_started_to')}
              {renderDateRangeSection('Field Work Completed Date', 'field_work_completed_from', 'field_work_completed_to')}
              {renderDateRangeSection('TA Review Date', 'ta_review_from', 'ta_review_to')}
              {renderDateRangeSection('Report Completed Date', 'report_completed_from', 'report_completed_to')}
              {renderDateRangeSection('Report Finalised Date', 'report_finalized_from', 'report_finalized_to')}
              {renderDateRangeSection('Send to Client Date', 'sent_to_client_from', 'sent_to_client_to')}
            </div>
          )}

          {/* Assignments Tab */}
          {activeTab === 'assignments' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Project Name (Search)</label>
                {renderProjectMultiSelect()}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Has Assigned MIS</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.has_assigned_mis || ''}
                  onChange={(e) => handleInputChange('has_assigned_mis', e.target.value)}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Blank Filter</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.blank_filter || ''}
                  onChange={(e) => handleInputChange('blank_filter', e.target.value)}
                >
                  <option value="">All Sites</option>
                  <option value="unassigned">Fully Unassigned</option>
                  <option value="any">Any Blank Assignment</option>
                  <option value="site_engineer">Site Engineer Blank</option>
                  <option value="ta">TA Blank</option>
                  <option value="mis">MIS Blank</option>
                  <option value="report_maker">Report Maker Blank</option>
                  <option value="validator">Validator Blank</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Has Assigned Engineer</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.has_assigned_engineer || ''}
                  onChange={(e) => handleInputChange('has_assigned_engineer', e.target.value)}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Has Assigned Technical Associate</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.has_assigned_ta || ''}
                  onChange={(e) => handleInputChange('has_assigned_ta', e.target.value)}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Has Assigned Report Maker</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.has_assigned_report_maker || ''}
                  onChange={(e) => handleInputChange('has_assigned_report_maker', e.target.value)}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Has Assigned Validator</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.has_assigned_validator || ''}
                  onChange={(e) => handleInputChange('has_assigned_validator', e.target.value)}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Site Engineer</label>
                <SelectWithSearch2
                  value={siteEngineerOptions.find(o => o.id === Number(filters.site_engineer_userid)) || null}
                  onChange={(option) => handleInputChange('site_engineer_userid', option ? String(option.id) : '')}
                  apiEndpoint="/users/list"
                  extraParams={{ src: 'all', role_id: [7, 4].join(',') }}
                  mapOption={(u: any) => ({ id: u.id, value: u.id, label: `${u.first_name} ${u.last_name}` })}
                  placeholder="Search site engineer (min 3 chars)"
                  minSearchLength={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Technical Associate</label>
                <SelectWithSearch2
                  value={taOptions.find(o => o.id === Number(filters.ta_userid)) || null}
                  onChange={(option) => handleInputChange('ta_userid', option ? String(option.id) : '')}
                  apiEndpoint="/users/list"
                  extraParams={{ src: 'all', role_id: '5' }}
                  mapOption={(u: any) => ({ id: u.id, value: u.id, label: `${u.first_name} ${u.last_name}` })}
                  placeholder="Search TA (min 3 chars)"
                  minSearchLength={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">MIS</label>
                <SelectWithSearch2
                  value={misOptions.find(o => o.id === Number(filters.mis_userid)) || null}
                  onChange={(option) => handleInputChange('mis_userid', option ? String(option.id) : '')}
                  apiEndpoint="/users/list"
                  extraParams={{ src: 'all', role_id: '6' }}
                  mapOption={(u: any) => ({ id: u.id, value: u.id, label: `${u.first_name} ${u.last_name}` })}
                  placeholder="Search MIS (min 3 chars)"
                  minSearchLength={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Report Maker</label>
                <SelectWithSearch2
                  value={reportMakerOptions.find(o => o.id === Number(filters.report_designer_userid)) || null}
                  onChange={(option) => handleInputChange('report_designer_userid', option ? String(option.id) : '')}
                  apiEndpoint="/users/list"
                  extraParams={{ src: 'all', role_id: '9' }}
                  mapOption={(u: any) => ({ id: u.id, value: u.id, label: `${u.first_name} ${u.last_name}` })}
                  placeholder="Search report maker (min 3 chars)"
                  minSearchLength={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Validator</label>
                <SelectWithSearch2
                  value={validatorOptions.find(o => o.id === Number(filters.validator_userid)) || null}
                  onChange={(option) => handleInputChange('validator_userid', option ? String(option.id) : '')}
                  apiEndpoint="/users/list"
                  extraParams={{ src: 'all', role_id: '10' }}
                  mapOption={(u: any) => ({ id: u.id, value: u.id, label: `${u.first_name} ${u.last_name}` })}
                  placeholder="Search validator (min 3 chars)"
                  minSearchLength={3}
                />
              </div>
            </div>
          )}

          {/* Stages Tab */}
          {activeTab === 'status' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Project Name (Search)</label>
                {renderProjectMultiSelect()}
              </div>

              {/* Same filter as on Site Info — shares its state, so picking an
                  activity on either tab shows up on both. */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Activity (Search)</label>
                <SelectWithSearch2
                  placeholder="Search activity (min 3 chars)"
                  value={selectedActivity}
                  onChange={(option) => {
                    setSelectedActivity(option);
                    handleSelectChange('activity_id', option);
                  }}
                  options={activities.map(a => ({
                    id: a.id,
                    label: `${a.activity_name} (${a.id})`,
                    value: a.id
                  }))}
                  className="w-full"
                  minSearchLength={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Work Completion Status</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.status || ''}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                >
                  <option value="">All</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="hold">On Hold</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Stage Group</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.stage_group || ''}
                  onChange={(e) => {
                    const group = e.target.value;
                    setSelectedWorkflowStatus(null);
                    onFiltersChange({
                      ...filters,
                      stage_group: group || undefined,
                      stage_filter: undefined,
                      survey_workflow_status: undefined,
                      workflow_status_list: group ? STAGE_GROUP_MAP[group] : undefined,
                      my_pending_stage: undefined,
                    });
                  }}
                >
                  <option value="">All Groups</option>
                  {STAGE_GROUP_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Site Stage / Workflow Stage</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.stage_filter || filters.survey_workflow_status || ''}
                  onChange={(e) => handleWorkflowStageChange(e.target.value)}
                >
                  <option value="">All Workflow Stages</option>
                  {WORKFLOW_STAGE_FILTERS.map((option) => (
                    <option key={option.value.toString()} value={option.value.toString()}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Site Priority</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.site_priority || ''}
                  onChange={(e) => handleInputChange('site_priority', e.target.value)}
                >
                  <option value="">All</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <button
            onClick={onReset}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded  hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors flex items-center gap-2"
          >
            <FiRefreshCw className="w-4 h-4" />
            Reset Filters
          </button>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded  hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                const dateError = filtersDateRangeError(filters);
                if (dateError) {
                  toast.error(dateError);
                  return;
                }
                onApply();
                onClose();
              }}
              className="px-6 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors flex items-center gap-2"
            >
              <FiSearch className="w-4 h-4" />
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface Pagination {
  offset: number
  limit: number
  page: number
  total_pages: number
  total_count: number
  has_next_page: boolean
  has_prev_page: boolean
  next_page: number | null
  prev_page: number | null
}

export interface PendingWorkCount {
  key: string
  label: string
  count: number
  statuses: string[]
}

interface SiteSortConfig {
  field: string
  direction: "ASC" | "DESC"
}

interface SitesListProps {
  sites: Site[]
  projects: Project[]
  activities: Activity[]
  users: { [roleId: number]: User[] }
  pagination: Pagination
  onSearch: (search: string) => void
  onStatusFilter: (status: string) => void
  onProjectFilter: (project_id: string) => void
  onActivityFilter: (activity_id: string) => void
  onPageChange: (page: number) => void
  onLimitChange: (limit: number) => void
  onSort: (field: string, direction: "ASC" | "DESC") => void
  onAddSite: () => void
  onEditSite: (site: Site) => void
  onAssignEngineer: (site: Site, data: any) => void
  onAssignTA: (site: Site, data: any) => void
  onAssignMIS: (site: Site, data: any) => void
  onAssignReportMaker: (site: Site, data: any) => void
  onAssignValidator: (site: Site, data: any) => void
  onCancelSite: (site: Site, reason: string) => void
  onHoldSite: (site: Site, reason: string) => void
  onResumeSite: (site: Site) => void
  onAdvancedFilter?: (filters: Filters) => void
  onBulkAssign?: (siteIds: number[], data: any, type: "reportMaker" | "validator") => void
  /**
   * Move a whole selection of finalized reports to Report Sent to Client — the
   * bulk form of the single site's "Send to Client" workflow action, and gated
   * on the same permission (Site / mis).
   */
  onBulkSendToClient?: (siteIds: number[], remarks?: string) => Promise<void> | void
  /** Cancel a selection of sites still at Site Created (Site / bulk_cancel). */
  onBulkCancel?: (siteIds: number[], reason: string) => Promise<void> | void
  loading?: boolean
  actionLoading?: boolean
  exportFilters?: Filters
  exportSort?: SiteSortConfig
  pendingWorkCounts?: PendingWorkCount[]
  /**
   * What the chip row is called. My Sites shows the viewer's own queue ("My
   * Pending Work"); Project Sites counts every site in the viewer's projects
   * waiting at that stage, which is not "mine", so it says "Pending Work".
   */
  pendingWorkTitle?: string
  /** Aged cancelled / completed sites — see the "Show all sites" toggle. */
  showAgedSites?: boolean
  onToggleAgedSites?: (next: boolean) => void
  agedSitesInfo?: { hidden: boolean; cancelled_days: number; completed_days: number } | null
  /**
   * My Sites: no filter modal at all - a single inline Project dropdown sits
   * where the Filters button would be. The caller passes only the viewer's own
   * assigned projects in `projects`.
   */
  projectFilterOnly?: boolean
}

export const ProjectSitesList: React.FC<SitesListProps> = ({
  sites,
  projects,
  activities,
  users,
  pagination,
  onSearch,
  onStatusFilter,
  onProjectFilter,
  onActivityFilter,
  onPageChange,
  onLimitChange,
  onSort,
  onAddSite,
  onEditSite,
  onAssignEngineer,
  onAssignTA,
  onAssignMIS,
  onAssignReportMaker,
  onAssignValidator,
  onCancelSite,
  onHoldSite,
  onResumeSite,
  onAdvancedFilter,
  onBulkAssign,
  onBulkSendToClient,
  onBulkCancel,
  loading = false,
  actionLoading = false,
  exportFilters,
  exportSort,
  pendingWorkCounts = [],
  pendingWorkTitle = "My Pending Work",
  showAgedSites = false,
  onToggleAgedSites,
  agedSitesInfo = null,
  projectFilterOnly = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [projectFilter, setProjectFilter] = useState("")
  const [activityFilter, setActivityFilter] = useState("")
  const [activitySearchTerm, setActivitySearchTerm] = useState("")
  const [showActivityDropdown, setShowActivityDropdown] = useState(false)
  const [showExcelModal, setShowExcelModal] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [exportData, setExportData] = useState<any[]>([])
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filters, setFilters] = useState<Filters>({});
  const [sortConfig, setSortConfig] = useState<SiteSortConfig>({
    field: "created_at",
    direction: "DESC",
  })

  const [filterUsers, setFilterUsers] = useState<User[]>([]);
  const [filterUsersLoading, setFilterUsersLoading] = useState(false);
  const filterUserSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchFilterUsers = async (search: string = '') => {
    setFilterUsersLoading(true);
    try {
      const params: any = { page: 1, limit: 10, src: 'all', role_id: [7, 4, 5, 6, 9, 10].join(',') };
      if (search) params.search = search;
      const response = await api.get('/users/list', { params });
      if (response.data.success) setFilterUsers(response.data.data.users || []);
    } catch (e) {
      console.error('Error fetching filter users:', e);
    } finally {
      setFilterUsersLoading(false);
    }
  };

  const onUserSearchChange = (term: string) => {
    if (filterUserSearchTimer.current) clearTimeout(filterUserSearchTimer.current);
    filterUserSearchTimer.current = setTimeout(() => fetchFilterUsers(term), 350);
  };

  const siteEngineerOptions: SelectOption[] = filterUsers.filter(u => u.role_id === 7 || u.role_id === 4).map(u => ({ id: u.id, label: `${u.first_name} ${u.last_name}`, value: u.id }));
  const taOptions: SelectOption[] = filterUsers.filter(u => u.role_id === 5).map(u => ({ id: u.id, label: `${u.first_name} ${u.last_name}`, value: u.id }));
  const misOptions: SelectOption[] = filterUsers.filter(u => u.role_id === 6).map(u => ({ id: u.id, label: `${u.first_name} ${u.last_name}`, value: u.id }));
  const reportMakerOptions: SelectOption[] = filterUsers.filter(u => u.role_id === 9).map(u => ({ id: u.id, label: `${u.first_name} ${u.last_name}`, value: u.id }));
  const validatorOptions: SelectOption[] = filterUsers.filter(u => u.role_id === 10).map(u => ({ id: u.id, label: `${u.first_name} ${u.last_name}`, value: u.id }));

  const [assignmentModal, setAssignmentModal] = useState<{
    isOpen: boolean
    type: "engineer" | "ta" | "mis" | "reportMaker" | "validator" | null
    site: Site | null
  }>({ isOpen: false, type: null, site: null })
  const [actionModal, setActionModal] = useState<{
    isOpen: boolean
    type: "cancel" | "hold" | "resume" | null
    site: Site | null
    reason: string
  }>({ isOpen: false, type: null, site: null, reason: "" })

  // Manual workflow stage override — see `canChangeWorkflowStage` below.
  const [stageModal, setStageModal] = useState<{
    isOpen: boolean
    site: Site | null
    toStatus: string
    remarks: string
    saving: boolean
  }>({ isOpen: false, site: null, toStatus: "", remarks: "", saving: false })

  const [selectedSites, setSelectedSites] = useState<number[]>([])
  const [showBulkForm, setShowBulkForm] = useState(false)
  const [bulkAssignType, setBulkAssignType] = useState<"reportMaker" | "validator" | null>(null)
  const [bulkSendingToClient, setBulkSendingToClient] = useState(false)
  const debouncedOnSearch = useDebouncedCallback(onSearch, 400)

  const { hasPermission, user } = useAuth()
  const { confirm, prompt } = useConfirm()
  const hasStandardAssignmentAccess = hasPermission("Site", "assign") || hasPermission("Site", "re_assign")
  const hasMisAssignmentAccess = hasPermission("Site", "mis")
  const canManageAssignments = hasStandardAssignmentAccess || hasMisAssignmentAccess
  /**
   * The manual stage override skips every workflow precondition, so it has its
   * own permission rather than riding on the assignment permissions. Nobody
   * holds it until it is granted to a role, which is why the button is absent
   * for everyone by default rather than present and refused by the API.
   */
  const canChangeWorkflowStage = hasPermission("Site", "change_workflow_stage")
  const activityDropdownRef = useRef<HTMLDivElement>(null)

  const submitStageChange = async () => {
    const site = stageModal.site
    if (!site || !stageModal.toStatus) return
    if (stageModal.toStatus === site.survey_workflow_status) {
      toast.error("Site is already at this workflow stage")
      return
    }
    setStageModal((prev) => ({ ...prev, saving: true }))
    try {
      const response = await api.put(`/survey/sites/${site.id}/workflow-stage`, {
        survey_workflow_status: stageModal.toStatus,
        remarks: stageModal.remarks.trim() || undefined,
      })
      if (response.data?.success) {
        toast.success("Workflow stage updated")
        setStageModal({ isOpen: false, site: null, toStatus: "", remarks: "", saving: false })
        onPageChange(pagination.page)
      } else {
        toast.error(response.data?.message || "Failed to change workflow stage")
        setStageModal((prev) => ({ ...prev, saving: false }))
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || error?.message || "Failed to change workflow stage",
      )
      setStageModal((prev) => ({ ...prev, saving: false }))
    }
  }

  const handleApplyFilters = () => {
    if (onAdvancedFilter) {
      onAdvancedFilter(filters);
    }
  };

  const handleResetFilters = () => {
    setFilters({});
    if (onAdvancedFilter) {
      onAdvancedFilter({});
    }
  };

  const applyPendingWorkFilter = (count: PendingWorkCount) => {
    const nextFilters = {
      ...filters,
      my_pending_stage: count.key,
      stage_group: undefined,
      stage_filter: undefined,
      survey_workflow_status: undefined,
      workflow_status_list: count.statuses.join(","),
    };
    // `filters` may hold a date range typed into the modal and never applied.
    const dateError = filtersDateRangeError(nextFilters);
    if (dateError) {
      toast.error(dateError);
      return;
    }
    setFilters(nextFilters);
    if (onAdvancedFilter) onAdvancedFilter(nextFilters);
  };

  const selectedWorkflowStatus = selectedSites.length
    ? sites.find(site => site.id === selectedSites[0])?.survey_workflow_status
    : undefined

  const hasMisBulk = hasPermission("Site", "mis")
  const hasBulkCancel = !!onBulkCancel && hasPermission("Site", "bulk_cancel")

  const canSelectSiteForBulk = (site: Site) => {
    // Bulk cancel is the only bulk action a non-MIS user has, so they may only
    // pick sites it can act on.
    if (!hasMisBulk && (site.survey_workflow_status !== "site_created" || site.status === "cancelled")) {
      return selectedSites.includes(site.id)
    }
    return !selectedWorkflowStatus || selectedSites.includes(site.id) || site.survey_workflow_status === selectedWorkflowStatus
  }

  const handleSelectSite = (siteId: number) => {
    const site = sites.find(item => item.id === siteId)
    if (site && !selectedSites.includes(siteId) && !canSelectSiteForBulk(site)) {
      return
    }

    setSelectedSites((prev) =>
      prev.includes(siteId)
        ? prev.filter((id) => id !== siteId)
        : [...prev, siteId]
    )
  }

  const handleSelectAll = () => {
    const selectableSites = sites.filter(site =>
      canSelectSiteForBulk(site) &&
      (!selectedWorkflowStatus || site.survey_workflow_status === selectedWorkflowStatus)
    )
    if (selectedSites.length === selectableSites.length) {
      setSelectedSites([])
    } else {
      setSelectedSites(selectableSites.map(site => site.id))
    }
  }

  const handleBulkAssign = (option: any) => {
    if (selectedSites.length > 0 && bulkAssignType && onBulkAssign) {
      onBulkAssign(selectedSites, { user: option }, bulkAssignType)
      setSelectedSites([])
      setShowBulkForm(false)
      setBulkAssignType(null)
    }
  }

  /**
   * Bulk "Send to Client". Only finalized reports can make the move, and the
   * selection is already locked to one workflow stage, so the button is only
   * offered when that stage is Report Finalized.
   */
  const canBulkSendToClient =
    !!onBulkSendToClient &&
    hasPermission("Site", "mis") &&
    selectedSites.length > 0 &&
    selectedWorkflowStatus === "report_finalized"

  const handleBulkSendToClient = async () => {
    if (!onBulkSendToClient || selectedSites.length === 0) return

    const count = selectedSites.length
    const ok = await confirm({
      title: `Send ${count} report${count === 1 ? "" : "s"} to the client?`,
      message: `${count} site${count === 1 ? "" : "s"} will move to Report Sent to Client and be marked completed.`,
      confirmText: "Send to Client",
      variant: "info",
    })
    if (!ok) return

    setBulkSendingToClient(true)
    try {
      await onBulkSendToClient(selectedSites)
      setSelectedSites([])
    } finally {
      setBulkSendingToClient(false)
    }
  }

  /**
   * Bulk cancel. Only sites still at Site Created qualify, and the selection is
   * locked to one workflow stage, so the button is only offered at that stage.
   */
  const canBulkCancel = hasBulkCancel && selectedSites.length > 0 && selectedWorkflowStatus === "site_created"
  const [bulkCancelling, setBulkCancelling] = useState(false)

  const handleBulkCancel = async () => {
    if (!onBulkCancel || selectedSites.length === 0) return

    const count = selectedSites.length
    const reason = await prompt({
      title: `Cancel ${count} site${count === 1 ? "" : "s"}?`,
      message: `${count} site${count === 1 ? "" : "s"} at Site Created will be cancelled. Sites already cancelled or further along are skipped.`,
      label: "Cancel reason",
      placeholder: "Why are these sites being cancelled?",
      required: true,
      multiline: true,
      confirmText: "Cancel sites",
      cancelText: "Go back",
      variant: "warning",
    })
    if (reason === null) return

    setBulkCancelling(true)
    try {
      await onBulkCancel(selectedSites, reason)
      setSelectedSites([])
    } finally {
      setBulkCancelling(false)
    }
  }

  const getBulkAssignProjectId = () => {
    const selectedSiteRecords = sites.filter((site) => selectedSites.includes(site.id))
    if (selectedSiteRecords.length === 0) return undefined

    return selectedSiteRecords[0]?.project_id
  }

  const getActiveFiltersCount = () => {
    return Object.keys(filters).filter(key => filters[key as keyof Filters]).length;
  };

  const handleSort = (field: string) => {
    const direction = sortConfig.field === field && sortConfig.direction === "ASC" ? "DESC" : "ASC"
    const newSort: SiteSortConfig = { field, direction }
    setSortConfig(newSort)
    onSort(field, direction)
  }

  const getSortIcon = (field: string) => {
    if (sortConfig.field !== field) return <FaSort className="w-3 h-3 text-gray-400" />
    return sortConfig.direction === "ASC" ? (
      <FaSortUp className="w-3 h-3 text-blue-800" />
    ) : (
      <FaSortDown className="w-3 h-3 text-blue-800" />
    )
  }


  useEffect(() => {
    if (isFilterModalOpen && filterUsers.length === 0) {
      fetchFilterUsers();
    }
  }, [isFilterModalOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (activityDropdownRef.current && !activityDropdownRef.current.contains(event.target as Node)) {
        setShowActivityDropdown(false)
      }
    }

    if (showActivityDropdown) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [showActivityDropdown])


  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchTerm(value)
    debouncedOnSearch(value)
  }

  const handleStatusFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    setStatusFilter(value)
    onStatusFilter(value)
  }

  const handleProjectFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    setProjectFilter(value)
    onProjectFilter(value)
  }

  const handleActivityFilterChange = (activityId: string) => {
    setActivityFilter(activityId)
    setActivitySearchTerm("")
    setShowActivityDropdown(false)
    onActivityFilter(activityId)
  }

  const getFilteredActivities = () => {
    if (!activitySearchTerm.trim()) return activities
    return activities.filter((activity) =>
      activity.activity_name.toLowerCase().includes(activitySearchTerm.toLowerCase()),
    )
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  /**
   * A date as the Excel export should carry it: exactly what the table on
   * screen shows. The export used to hand the raw column through, so every
   * date column arrived as an ISO timestamp ("2026-09-05T04:00:00.000Z")
   * while the same value read "Sep 5, 2026, 09:30 AM" in the portal. Empty
   * stays empty, and an unparseable value is passed through untouched rather
   * than becoming "Invalid Date".
   */
  const exportDate = (value: unknown): string => {
    if (value === null || value === undefined || value === "") return ""
    const parsed = new Date(String(value))
    return Number.isNaN(parsed.getTime()) ? String(value) : formatDate(String(value))
  }

  const isRestrictedSiteExport = user?.role?.id === 9 || user?.role?.id === 10
  const isReportMaker = user?.role?.id === 9
  const isSiteEngineer = user?.role?.id === 7
  // The selection column serves every bulk action: MIS assignments and bulk cancel.
  const canBulkSelect = (hasMisBulk || hasBulkCancel) && !isReportMaker

  const mapRestrictedSiteForExport = (site: any) => ({
    site_id: site.site_id || "",
    project_short_name: site.project?.project_short_name || "",
    activity_name: site.activity?.activity_name || "",
    site_name: site.site_name || "",
    site_address: site.site_address || "",
    opco_name: site.opco_name || "",
    opco_id: site.opco_id || "",
    state_name: site.state_circle?.state_name || "",
    pincode: site.pincode || "",
    site_latitude: site.site_latitude || "",
    site_longitude: site.site_longitude || "",
    site_priority: site.site_priority || "",
    customer_requirement: site.customer_requirement || "",
    site_information1: site.site_information1 || "",
    site_information2: site.site_information2 || "",
    site_information3: site.site_information3 || "",
    mis: getMisDisplayName(site),
    report_maker: site.report_designer ? `${site.report_designer.first_name} ${site.report_designer.last_name}` : "",
    // /survey/sites/all does not return workflow_status_dates; the report
    // completed date it does return is report_designer_work_date.
    report_completed_date: exportDate(site.workflow_status_dates?.report_completed || site.report_designer_work_date),
    status: site.status || "",
    survey_workflow_status: site.survey_workflow_status || "",
  })

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      site_created: {
        color: "bg-emerald-100 text-emerald-800",
        icon: FaCheckCircle,
        text: "Site Created",
      },
      engineer_assigned: {
        color: "bg-sky-100 text-sky-800",
        icon: FaUser,
        text: "Engineer Assigned",
      },
      field_work_in_progress: {
        color: "bg-amber-100 text-amber-800",
        icon: FaClock,
        text: "Field Work In Progress",
      },
      field_work_completed: {
        color: "bg-lime-100 text-lime-800",
        icon: FaCheckCircle,
        text: "Field Work Completed",
      },
      data_uploaded: {
        color: "bg-teal-100 text-teal-800",
        icon: FaCheckCircle,
        text: "Site Data WIP",
      },
      ta_review_pending: {
        color: "bg-orange-100 text-orange-800",
        icon: FaClock,
        text: "TA Review Pending",
      },
      ta_review_completed: {
        color: "bg-violet-100 text-violet-800",
        icon: FaCheckCircle,
        text: "TA Review Completed",
      },
      ta_rejected: {
        color: "bg-rose-100 text-rose-800",
        icon: FaTimesCircle,
        text: "TA Review Rejected",
      },
      mis_report_assign_pending: {
        color: "bg-yellow-100 text-yellow-800",
        icon: FaClock,
        text: "MIS Report Assign Pending",
      },
      report_maker_assigned: {
        color: "bg-indigo-100 text-indigo-800",
        icon: FaUser,
        text: "Report Maker Assigned",
      },
      report_in_progress: {
        color: "bg-fuchsia-100 text-fuchsia-800",
        icon: FaClock,
        text: "Report In Progress",
      },
      report_completed: {
        color: "bg-cyan-100 text-cyan-800",
        icon: FaCheckCircle,
        text: "Report Completed",
      },
      report_maker_rejected: {
        color: "bg-red-100 text-red-800",
        icon: FaTimesCircle,
        text: "Report Maker Rejected",
      },
      validator_assigned: {
        color: "bg-blue-100 text-blue-800",
        icon: FaUser,
        text: "Validator Assigned",
      },
      validation_in_progress: {
        color: "bg-violet-100 text-violet-800",
        icon: FaClock,
        text: "Validation In Progress",
      },
      validation_completed: {
        color: "bg-emerald-200 text-emerald-900",
        icon: FaCheckCircle,
        text: "Validation Completed",
      },
      validation_rejected: {
        color: "bg-pink-100 text-pink-800",
        icon: FaTimesCircle,
        text: "Validation Rejected",
      },
      report_finalized: {
        color: "bg-green-200 text-green-900",
        icon: FaCheckCircle,
        text: "Report Finalized",
      },
      report_sent_to_client: {
        color: "bg-teal-200 text-teal-900",
        icon: FaCheckCircle,
        text: "Report Sent To Client",
      },
      cancelled: {
        color: "bg-red-200 text-red-900",
        icon: FaCheckCircle,
        text: "Cancelled",
      },
      hold: {
        color: "bg-stone-200 text-stone-900",
        icon: FaCheckCircle,
        text: "On Hold",
      },
      completed: {
        color: "bg-green-200 text-green-900",
        icon: FaCheckCircle,
        text: "Completed",
      },
      in_progress: {
        color: "bg-yellow-200 text-yellow-900",
        icon: FaHourglass,
        text: "In Progress",
      }
    }

    const config = statusConfig[status as keyof typeof statusConfig] || {
      color: "bg-gray-100 text-gray-800 capitalize",
      icon: FaMapMarkerAlt,
      text: status.replace(/_/g, " "),
    }

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <config.icon className="w-2 h-2" />
        {config.text}
      </span>
    )
  }

  // Engineer can be assigned when site is created and not cancelled (MIS cannot assign/reassign engineer)
  const canAssignEngineer = (site: Site) => {
    return hasStandardAssignmentAccess &&
      (site.survey_workflow_status === "site_created" || site.survey_workflow_status === "engineer_assigned") &&
      site.status !== "cancelled" &&
      !site.site_engineer
  }

  // Engineer can be reassigned when already assigned (MIS cannot reassign engineer)
  const canReassignEngineer = (site: Site) => {
    if (site.survey_workflow_status === "report_sent_to_client") return false
    return hasStandardAssignmentAccess &&
      (site.survey_workflow_status === "site_created" || site.survey_workflow_status === "engineer_assigned") &&
      site.status !== "cancelled" &&
      !!site.site_engineer
  }

  // TA can be reassigned when TA is already assigned but hasn't completed review (MIS cannot reassign TA)
  const canReassignTA = (site: Site) => {
    if (site.survey_workflow_status === "report_sent_to_client") return false
    const taWorkDoneStatuses = [
      "ta_review_completed", "mis_report_assign_pending", "report_maker_assigned",
      "report_in_progress", "report_completed", "report_maker_rejected",
      "validator_assigned", "validation_in_progress", "validation_completed",
      "validation_rejected", "report_finalized", "report_sent_to_client"
    ]
    return hasStandardAssignmentAccess &&
      !!site.ta &&
      !taWorkDoneStatuses.includes(site.survey_workflow_status)
  }

  // MIS can be reassigned when MIS is already assigned
  const canReassignMIS = (site: Site) => {
    if (site.survey_workflow_status === "report_sent_to_client") return false
    return canManageAssignments &&
      !!(site.mis || site.mis_userid || site.mis_name || site.mis_username)
  }

  // Report Maker can be assigned when TA review is completed
  const canAssignReportMaker = (site: Site) => {
    return canManageAssignments &&
      site.survey_workflow_status === "ta_review_completed" &&
      !site.report_designer
  }

  // Report Maker can be reassigned when already assigned but hasn't completed the report
  const canReassignReportMaker = (site: Site) => {
    if (site.survey_workflow_status === "report_sent_to_client") return false
    const reportMakerWorkDoneStatuses = [
      "report_completed", "validator_assigned", "validation_in_progress",
      "validation_completed", "validation_rejected", "report_finalized",
      "report_sent_to_client"
    ]
    return canManageAssignments &&
      !!site.report_designer &&
      !reportMakerWorkDoneStatuses.includes(site.survey_workflow_status)
  }

  // Validator can be assigned when report is completed
  const canAssignValidator = (site: Site) => {
    return canManageAssignments &&
      site.survey_workflow_status === "report_completed" &&
      !site.validator
  }

  // Validator can be reassigned when already assigned but hasn't completed validation
  const canReassignValidator = (site: Site) => {
    if (site.survey_workflow_status === "report_sent_to_client") return false
    const validatorWorkDoneStatuses = [
      "validation_completed", "report_finalized", "report_sent_to_client"
    ]
    return canManageAssignments &&
      !!site.validator &&
      !validatorWorkDoneStatuses.includes(site.survey_workflow_status)
  }

  const canCancelSite = (site: Site) => {
    return hasPermission("Site", "cancel") && site.status !== "report_submitted" && !["field_work_in_progress", "data_uploaded", "ta_review_pending", "ta_review_completed", "ta_rejected", "mis_report_assign_pending", "report_maker_assigned", "report_in_progress", "report_completed", "report_maker_rejected", "validator_assigned", "validation_in_progress", "validation_completed", "validation_rejected", "report_finalized", "report_sent_to_client"].includes(site.survey_workflow_status)
  }

  const canHoldSite = (site: Site) => {
    if (site.survey_workflow_status === "report_sent_to_client") return false
    return hasPermission("Site", "hold") && !["report_submitted", "cancelled"].includes(site.status) && !["field_work_in_progress", "data_uploaded", "ta_review_pending", "ta_review_completed", "ta_rejected", "mis_report_assign_pending", "report_maker_assigned", "report_in_progress", "report_completed", "report_maker_rejected", "validator_assigned", "validation_in_progress", "validation_completed", "validation_rejected", "report_finalized", "report_sent_to_client"].includes(site.survey_workflow_status)
  }

  const handleAssignment = (type: "engineer" | "ta" | "mis" | "reportMaker" | "validator", site: Site) => {
    if (!canManageAssignments) return
    setAssignmentModal({ isOpen: true, type, site })
  }

  const handleAction = (type: "cancel" | "hold" | "resume", site: Site) => {
    setActionModal({ isOpen: true, type, site, reason: "" })
  }

  const executeAssignment = (data: any) => {
    if (!assignmentModal.site) return

    switch (assignmentModal.type) {
      case "engineer":
        onAssignEngineer(assignmentModal.site, data)
        break
      case "ta":
        onAssignTA(assignmentModal.site, data)
        break
      case "mis":
        onAssignMIS(assignmentModal.site, data)
        break
      case "reportMaker":
        onAssignReportMaker(assignmentModal.site, data)
        break
      case "validator":
        onAssignValidator(assignmentModal.site, data)
        break
    }
    setAssignmentModal({ isOpen: false, type: null, site: null })
  }

  const fetchExportData = async () => {
    try {
      setExporting(true)
      const activeSort = exportSort || sortConfig
      const baseParams: any = {
        sort_by: activeSort?.field,
        sort_order: activeSort?.direction,
        ...(exportFilters || {}),
      }
      if (searchTerm.trim()) baseParams.search = searchTerm.trim()
      if (statusFilter.trim()) baseParams.status = statusFilter.trim()
      if (projectFilter.trim()) baseParams.project_id = projectFilter.trim()
      if (activityFilter.trim()) baseParams.activity_id = activityFilter.trim()
      // The export must carry the same aged-site rule the table is showing,
      // or the workbook silently holds rows the screen does not.
      if (showAgedSites) baseParams.show_aged_sites = "true"
      Object.keys(baseParams).forEach((key) => {
        if (baseParams[key] === undefined || baseParams[key] === null || baseParams[key] === "") {
          delete baseParams[key]
        }
      })

      const pageLimit = 10
      let page = 1
      let totalPages = 1
      const exportSites: Site[] = []

      do {
        const response = await api.get("/survey/sites/all", {
          params: { ...baseParams, page, limit: pageLimit },
        })

        if (!response.data.success) break

        const pageSites: Site[] = response.data.data.sites || []
        exportSites.push(...pageSites)

        const paginationInfo = response.data.data.pagination
        if (paginationInfo) {
          totalPages =
            paginationInfo.total_pages ??
            (paginationInfo.total_count ? Math.ceil(paginationInfo.total_count / pageLimit) : totalPages)
        } else if (pageSites.length < pageLimit) {
          totalPages = page
        }

        page += 1
      } while (page <= totalPages)

      const mapped = isRestrictedSiteExport
        ? exportSites.map(mapRestrictedSiteForExport)
        : exportSites.map((site: any) => {
          const base: any = {
          // --- Columns in UI table order ---
          site_id: site.site_id,
          status: site.status || "",
          project_name: site.project?.project_name || "",
          project_short_name: site.project?.project_short_name || "",
          survey_workflow_status: site.survey_workflow_status || "",
          client: site.project?.client?.client_short_name || "",
          activity_name: site.activity?.activity_name || "",
          team_coordinator: site.team_coordinator
            ? `${site.team_coordinator.first_name} ${site.team_coordinator.last_name}`
            : "",
          site_engineer: site.site_engineer ? `${site.site_engineer.first_name} ${site.site_engineer.last_name}` : "",
          ta: site.ta ? `${site.ta.first_name} ${site.ta.last_name}` : "",
          mis: getMisDisplayName(site),
          site_priority: site.site_priority || "",
          visit_remarks: site.visit_remarks || "",
          created_at: exportDate(site.created_at),
          site_engineer_assign_date: exportDate(site.site_engineer_assign_date),
          field_work_started_date: exportDate(site.field_work_started_date || site.site_unlock_time),
          first_data_upload_date: exportDate(site.first_data_upload_date),
          field_work_completed_date: exportDate(site.field_work_completed_date),
          ta_work_date: exportDate(site.ta_work_date),
          updated_at: exportDate(site.updated_at),
          // --- Additional detail columns ---
          reference_number: site.reference_number || "",
          client_allocation_date: exportDate(site.client_allocation_date),
          site_name: site.site_name || "",
          site_address: site.site_address || "",
          opco_name: site.opco_name || "",
          opco_id: site.opco_id || "",
          state_name: site.state_circle?.state_name || "",
          district_name: site.district?.name || "",
          pincode: site.pincode || "",
          site_latitude: site.site_latitude || "",
          site_longitude: site.site_longitude || "",
          technician_name: site.technician_name || "",
          technician_number: site.technician_number || "",
          fse_name: site.fse_name || "",
          fse_number: site.fse_number || "",
          aom_name: site.aom_name || "",
          aom_number: site.aom_number || "",
          customer_requirement: site.customer_requirement || "",
          site_information1: site.site_information1 || "",
          site_information2: site.site_information2 || "",
          site_information3: site.site_information3 || "",
          created_by: site.creator ? `${site.creator.first_name} ${site.creator.last_name}` : site.created_by,
          is_location_verified: site.is_location_verified ? "Yes" : "No",
          location_verification_attempts: site.location_verification_attempts ?? "",
          source: site.source || "",
          }
          if (!isSiteEngineer) {
            base.report_maker = site.report_designer ? `${site.report_designer.first_name} ${site.report_designer.last_name}` : ""
            base.validator = site.validator ? `${site.validator.first_name} ${site.validator.last_name}` : ""
            base.report_designer_work_date = exportDate(site.report_designer_work_date)
            base.report_completed_date = exportDate(site.workflow_status_dates?.report_completed || site.report_designer_work_date)
            base.report_finalized_date = exportDate(site.report_finalized_date || site.validator_work_date)
            base.report_sent_to_client_date = exportDate(site.report_sent_to_client_date)
          }
          return base
        })
      
      setExportData(mapped)
      setShowExcelModal(true)
    } catch (error) {
      console.error("Error exporting sites:", error)
    } finally {
      setExporting(false)
    }
  }

  const getAssignmentUsers = () => {
    switch (assignmentModal.type) {
      case "engineer":
        return users[2] || [] // Engineers
      case "reportMaker":
        return users[5] || [] // Report Makers
      case "validator":
        return users[6] || [] // Validators
      default:
        return []
    }
  }

  return (
    <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
      {/* Header */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h2 className="text-xl font-semibold text-gray-900">Sites Management</h2>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <button
              onClick={fetchExportData}
              disabled={exporting}
              className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm"
            >
              <FaFileExcel className="w-4 h-4" />
              {exporting ? "Preparing..." : "Export Excel"}
            </button>
            {hasPermission('Site', 'create') && (
              <button
                onClick={onAddSite}
                className="bg-blue-800 hover:bg-blue-950 text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm"
              >
                <FaPlus className="w-4 h-4" />
                Add New Site
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50/30">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <label className="block text-xs font-medium text-gray-700 mb-1">Search Sites</label>
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by site ID or project name"
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
          </div>

          {/* Status Filter */}
          {/* <div className="w-full sm:w-48">
            <label className="block text-xs font-medium text-gray-700 mb-1">Status Filter</label>
            <div className="relative">
              <FaFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
              <select
                value={statusFilter}
                onChange={handleStatusFilterChange}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm appearance-none bg-white"
              >
                <option value="">All Statuses</option>
                <option value="site_unassign">Unassigned</option>
                <option value="engineer_assigned">Engineer Assigned</option>
                <option value="field_work_done">Field Work Done</option>
                <option value="ta_done">TA Done</option>
                <option value="report_assign">Report Assigned</option>
                <option value="report_done">Report Done</option>
                <option value="validation_assign">Validation Assigned</option>
                <option value="validation_done">Validation Done</option>
                <option value="report_submitted">Submitted</option>
                <option value="cancelled">Cancelled</option>
                <option value="hold">On Hold</option>
              </select>
            </div>
          </div> */}

          {/* Project Filter */}
          {/* <div className="w-full sm:w-48">
            <label className="block text-xs font-medium text-gray-700 mb-1">Project Filter</label>
            <div className="relative">
              <FaProjectDiagram className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
              <select
                value={projectFilter}
                onChange={handleProjectFilterChange}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm appearance-none bg-white"
              >
                <option value="">All Projects</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.project_name}
                  </option>
                ))}
              </select>
            </div>
          </div> */}

          {/* Activity Filter - Searchable */}
          {/* <div className="w-full sm:w-48">
            <label className="block text-xs font-medium text-gray-700 mb-1">Activity Filter</label>
            <div className="relative" ref={activityDropdownRef}>
              <FaTasks className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none z-10" />
              <input
                type="text"
                placeholder={
                  activityFilter
                    ? activities.find((a) => a.id.toString() === activityFilter)?.activity_name || "Search..."
                    : "Search activities..."
                }
                value={activitySearchTerm}
                onChange={(e) => setActivitySearchTerm(e.target.value)}
                onFocus={() => setShowActivityDropdown(true)}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
              />
              {showActivityDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded shadow-lg z-20 max-h-48 overflow-y-auto">
                  <button
                    type="button"
                    onClick={() => handleActivityFilterChange("")}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 transition-colors ${!activityFilter ? "bg-blue-50 text-blue-700 font-medium" : "text-gray-700"
                      }`}
                  >
                    All Activities
                  </button>
                  {getFilteredActivities().length > 0 ? (
                    getFilteredActivities().map((activity) => (
                      <button
                        key={activity.id}
                        type="button"
                        onClick={() => handleActivityFilterChange(activity.id.toString())}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 transition-colors ${activityFilter === activity.id.toString()
                          ? "bg-blue-50 text-blue-700 font-medium"
                          : "text-gray-700"
                          }`}
                      >
                        {activity.activity_name}
                      </button>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-500 italic">No activities found</div>
                  )}
                </div>
              )}
            </div>
          </div> */}

          {/* Show all sites — lifts the aged cancelled / completed cut-off.
              Only rendered for a role that holds Site / view_aged_sites; every
              other user just sees the filtered list, which is the default. */}
          {hasPermission('Site', 'view_aged_sites') && onToggleAgedSites && (
            <div className="w-full sm:w-auto">
              <label className="block text-xs font-medium text-gray-700 mb-1">&nbsp;</label>
              <button
                type="button"
                onClick={() => onToggleAgedSites(!showAgedSites)}
                aria-pressed={showAgedSites}
                title={
                  showAgedSites
                    ? 'Showing every site, including aged cancelled and completed ones'
                    : `Hiding cancelled sites older than ${agedSitesInfo?.cancelled_days ?? 31} days and completed sites sent to client over ${agedSitesInfo?.completed_days ?? 185} days ago`
                }
                className={`w-full sm:w-auto px-4 py-2 border rounded flex items-center justify-center gap-2 transition-colors text-sm ${
                  showAgedSites
                    ? 'bg-blue-800 border-blue-800 text-white hover:bg-blue-900'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {showAgedSites ? <FiEye className="w-4 h-4" /> : <FiEyeOff className="w-4 h-4" />}
                {showAgedSites ? 'Showing All Sites' : 'Show All Sites'}
              </button>
            </div>
          )}

          {projectFilterOnly ? (
            <div className="w-full sm:w-64">
              <label className="block text-xs font-medium text-gray-700 mb-1">Project</label>
              <div className="relative">
                <FaProjectDiagram className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                <select
                  value={projectFilter}
                  onChange={handleProjectFilterChange}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm appearance-none bg-white"
                >
                  <option value="">All My Projects</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.project_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ) : (
          /* Advanced Filter Button */
          <div className="w-full sm:w-auto">
            <label className="block text-xs font-medium text-gray-700 mb-1">&nbsp;</label>
            <button
              onClick={() => setIsFilterModalOpen(true)}
              className="w-full sm:w-auto px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 flex items-center justify-center gap-2 transition-colors text-sm relative"
            >
              <FiFilter className="w-4 h-4" />
              Filters
              {getActiveFiltersCount() > 0 && (
                <span className="absolute -top-2 -right-2 bg-blue-800 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {getActiveFiltersCount()}
                </span>
              )}
            </button>
          </div>
          )}
        </div>

        {pendingWorkCounts.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-gray-700">{pendingWorkTitle}:</span>
              {pendingWorkCounts.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => applyPendingWorkFilter(item)}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded border text-xs font-medium transition-colors ${
                    filters.my_pending_stage === item.key
                      ? 'border-blue-300 bg-blue-50 text-blue-700'
                      : 'border-gray-200 bg-white text-gray-700 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700'
                  }`}
                >
                  <span>{item.label}</span>
                  <span className="min-w-5 h-5 px-1.5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center">
                    {item.count}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Active Filters Display */}
        {getActiveFiltersCount() > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-gray-700">Active Filters:</span>
              {Object.entries(filters).map(([key, value]) =>
                value && (
                  <span key={key} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {key.replace(/_/g, ' ')}: {value}
                    <button
                      onClick={() => {
                        const newFilters = { ...filters };
                        delete newFilters[key as keyof Filters];
                        setFilters(newFilters);
                        const dateError = filtersDateRangeError(newFilters);
                        if (dateError) {
                          toast.error(dateError);
                          return;
                        }
                        if (onAdvancedFilter) {
                          onAdvancedFilter(newFilters);
                        }
                      }}
                      className="ml-1 text-blue-600 hover:text-blue-800"
                    >
                      <FiX className="w-3 h-3" />
                    </button>
                  </span>
                )
              )}
              <button
                onClick={handleResetFilters}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                Clear all
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Actions */}
      {canBulkSelect && selectedSites.length > 0 && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded p-4 mb-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <FaUserCheck className="w-5 h-5 text-blue-800" />
                <span className="text-sm font-semibold text-blue-900">
                  {selectedSites.length} site{selectedSites.length !== 1 ? 's' : ''} selected
                  {selectedWorkflowStatus ? ` (${selectedWorkflowStatus.replace(/_/g, ' ')})` : ''}
                </span>
              </div>
              <div className="h-4 w-px bg-gray-300"></div>
              <button
                onClick={() => setSelectedSites([])}
                className="text-xs text-gray-500 hover:text-gray-700 underline"
              >
                Clear selection
              </button>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {hasMisBulk && (
                <>
                  <button
                    onClick={() => { setBulkAssignType("reportMaker"); setShowBulkForm(true); }}
                    className="bg-blue-800 text-white px-4 py-2 rounded hover:bg-blue-900 text-sm font-medium transition-colors flex items-center gap-2"
                  >
                    <FaFileAlt className="w-4 h-4" />
                    Bulk Assign Report Maker
                  </button>
                  <button
                    onClick={() => { setBulkAssignType("validator"); setShowBulkForm(true); }}
                    className="bg-indigo-700 text-white px-4 py-2 rounded hover:bg-indigo-800 text-sm font-medium transition-colors flex items-center gap-2"
                  >
                    <FaCheckCircle className="w-4 h-4" />
                    Bulk Assign Validator
                  </button>
                </>
              )}
              {canBulkCancel && (
                <button
                  onClick={handleBulkCancel}
                  disabled={bulkCancelling || actionLoading}
                  className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <FaBan className="w-4 h-4" />
                  {bulkCancelling ? "Cancelling…" : "Bulk Cancel"}
                </button>
              )}
              {canBulkSendToClient && (
                <button
                  onClick={handleBulkSendToClient}
                  disabled={bulkSendingToClient}
                  className="bg-green-700 text-white px-4 py-2 rounded hover:bg-green-800 text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <FaPaperPlane className="w-4 h-4" />
                  {bulkSendingToClient ? "Sending…" : "Mark Report Sent to Client"}
                </button>
              )}
            </div>
          </div>

          {showBulkForm && bulkAssignType && (
            <div className="mt-4 p-4 bg-white rounded border border-gray-200 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                Assign {bulkAssignType === "reportMaker" ? "Report Maker" : "Validator"} to {selectedSites.length} site{selectedSites.length !== 1 ? 's' : ''}
              </h3>
              <div className="max-w-md">
                <SelectWithSearch2
                  placeholder={`Select ${bulkAssignType === "reportMaker" ? "Report Maker" : "Validator"}`}
                  value={null}
                  onChange={(option) => option && handleBulkAssign(option)}
                  apiEndpoint="/survey/sites/assignment/users"
                  extraParams={{
                    role_id: bulkAssignType === "reportMaker" ? [9] : [10, 5, 4, 6, 3],
                    project_id: getBulkAssignProjectId(),
                    src: "all",
                    exclude_user_id: user?.id,
                  }}
                  mapOption={(user: any) => ({
                    id: user.id,
                    value: user.id,
                    label: `${user.first_name} ${user.last_name} (${user.username}) - ${user?.employee_code || 'N/A'}`,
                  })}
                  className="w-full"
                />
              </div>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => { setShowBulkForm(false); setBulkAssignType(null); }}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {canBulkSelect && (
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    checked={selectedSites.length > 0 && sites.length > 0 && sites.every(site => !canSelectSiteForBulk(site) || selectedSites.includes(site.id))}
                    onChange={handleSelectAll}
                  />
                </th>
              )}
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[200px]">
                Actions
              </th>
              {!isReportMaker && (
                <>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">
                <button
                  type="button"
                  onClick={() => handleSort("site_id")}
                  className="flex items-center gap-2 text-gray-600 hover:text-blue-800 uppercase text-xs font-semibold"
                >
                  Site ID
                  {getSortIcon("site_id")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">
                <button
                  type="button"
                  onClick={() => handleSort("status")}
                  className="flex items-center gap-2 text-gray-600 hover:text-blue-800 uppercase text-xs font-semibold"
                >
                  Work Completion Status
                  {getSortIcon("status")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[200px]">
                Project Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">
                <button
                  type="button"
                  onClick={() => handleSort("survey_workflow_status")}
                  className="flex items-center gap-2 text-gray-600 hover:text-blue-800 uppercase text-xs font-semibold"
                >
                  Site Stage
                  {getSortIcon("survey_workflow_status")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">
                Client
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">
                Activity
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">
                Team Coordinator
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">
                <button
                  type="button"
                  onClick={() => handleSort("engineer_name")}
                  className="flex items-center gap-2 text-gray-600 hover:text-blue-800 uppercase text-xs font-semibold"
                >
                  Site Engineer
                  {getSortIcon("engineer_name")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">
                <button
                  type="button"
                  onClick={() => handleSort("ta_name")}
                  className="flex items-center gap-2 text-gray-600 hover:text-blue-800 uppercase text-xs font-semibold"
                >
                  Technical Assistant
                  {getSortIcon("ta_name")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">
                <button
                  type="button"
                  onClick={() => handleSort("mis_name")}
                  className="flex items-center gap-2 text-gray-600 hover:text-blue-800 uppercase text-xs font-semibold"
                >
                  MIS
                  {getSortIcon("mis_name")}
                </button>
              </th>
              {!isSiteEngineer && (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">
                  <button
                    type="button"
                    onClick={() => handleSort("report_maker_name")}
                    className="flex items-center gap-2 text-gray-600 hover:text-blue-800 uppercase text-xs font-semibold"
                  >
                    Report Maker
                    {getSortIcon("report_maker_name")}
                  </button>
                </th>
              )}
              {!isSiteEngineer && (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">
                  <button
                    type="button"
                    onClick={() => handleSort("validator_name")}
                    className="flex items-center gap-2 text-gray-600 hover:text-blue-800 uppercase text-xs font-semibold"
                  >
                    Validator
                    {getSortIcon("validator_name")}
                  </button>
                </th>
              )}
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">
                Site Priority
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[200px]">
                <button
                  type="button"
                  onClick={() => handleSort("visit_remarks")}
                  className="flex items-center gap-2 text-gray-600 hover:text-blue-800 uppercase text-xs font-semibold"
                >
                  Visit Remarks
                  {getSortIcon("visit_remarks")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[170px]">
                <button
                  type="button"
                  onClick={() => handleSort("created_at")}
                  className="flex items-center gap-2 text-gray-600 hover:text-blue-800 uppercase text-xs font-semibold"
                >
                  Site Creation Date
                  {getSortIcon("created_at")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[170px]">
                <button
                  type="button"
                  onClick={() => handleSort("site_engineer_assign_date")}
                  className="flex items-center gap-2 text-gray-600 hover:text-blue-800 uppercase text-xs font-semibold"
                >
                  SE Assign Date
                  {getSortIcon("site_engineer_assign_date")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[170px]">
                <button
                  type="button"
                  onClick={() => handleSort("site_unlock_time")}
                  className="flex items-center gap-2 text-gray-600 hover:text-blue-800 uppercase text-xs font-semibold"
                >
                  Field Work Start Date
                  {getSortIcon("site_unlock_time")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[170px]">
                First Data Upload Date
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[170px]">
                Field Work Complete Date
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">
                <button
                  type="button"
                  onClick={() => handleSort("ta_work_date")}
                  className="flex items-center gap-2 text-gray-600 hover:text-blue-800 uppercase text-xs font-semibold"
                >
                  TA Review Date
                  {getSortIcon("ta_work_date")}
                </button>
              </th>
              {!isSiteEngineer && (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[170px]">
                  <button
                    type="button"
                    onClick={() => handleSort("report_designer_work_date")}
                    className="flex items-center gap-2 text-gray-600 hover:text-blue-800 uppercase text-xs font-semibold"
                  >
                    Report Prepare Date
                    {getSortIcon("report_designer_work_date")}
                  </button>
                </th>
              )}
              {!isSiteEngineer && (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[170px]">
                  <button
                    type="button"
                    onClick={() => handleSort("validator_work_date")}
                    className="flex items-center gap-2 text-gray-600 hover:text-blue-800 uppercase text-xs font-semibold"
                  >
                    Report Finalize Date
                    {getSortIcon("validator_work_date")}
                  </button>
                </th>
              )}
              {!isSiteEngineer && (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[180px]">
                  Report Sent to Client Date
                </th>
              )}
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">
                <button
                  type="button"
                  onClick={() => handleSort("updated_at")}
                  className="flex items-center gap-2 text-gray-600 hover:text-blue-800 uppercase text-xs font-semibold"
                >
                  Updated At
                  {getSortIcon("updated_at")}
                </button>
              </th>
                </>
              )}
              {isReportMaker && (
                <>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">
                    site_id
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[170px]">project_short_name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">activity_name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[200px]">
                    site_name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[250px]">
                    site_address
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">
                    opco_name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">
                    opco_id
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">
                    state_name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">pincode</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">
                    site_latitude
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">
                    site_longitude
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">site_priority</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[200px]">
                    customer_requirement
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[200px]">
                    site_information1
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[200px]">
                    site_information2
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[200px]">
                    site_information3
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">mis</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">report_maker</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[170px]">report_completed_date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">survey_workflow_status</th>
                </>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                  <td colSpan={isReportMaker || isSiteEngineer ? 22 : 27} className="px-4 sm:px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
                    <span className="text-sm text-gray-600">Loading sites...</span>
                  </div>
                </td>
              </tr>
            ) : sites.length === 0 ? (
              <tr>
                  <td colSpan={isReportMaker || isSiteEngineer ? 22 : 27} className="px-4 sm:px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                      <FaMapMarkerAlt className="w-6 h-6 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">No sites found</p>
                    <p className="text-xs text-gray-400 mt-1">Try adjusting your search or create a new site</p>
                  </div>
                </td>
              </tr>
            ) : (
              sites.map((site) => (
                <tr key={site.id} className="hover:bg-gray-50 transition-colors">
                  {canBulkSelect && (
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        checked={selectedSites.includes(site.id)}
                        disabled={!canSelectSiteForBulk(site)}
                        onChange={() => handleSelectSite(site.id)}
                        title={!canSelectSiteForBulk(site) ? (hasMisBulk ? 'Bulk actions require the same workflow stage' : 'Only sites at Site Created can be bulk cancelled') : 'Select site'}
                      />
                    </td>
                  )}
                  {/* Actions */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1 flex-wrap">
                      {hasPermission('Site', 'view') && (
                        <Link
                          target="_blank"
                          to={`/sites/${site.id}`}
                          className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded transition-colors"
                          title="Site ID"
                        >
                          <LuEye className="w-4 h-4" />
                        </Link>
                      )}
                      {hasPermission('Site', 'edit') && (
                        <Link
                          to={`/sites/${site.id}/update`}
                          className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded transition-colors"
                          title="Edit site"
                        >
                          <FaEdit className="w-4 h-4" />
                        </Link>
                      )}

                      {/* {site.has_pending_billing && (
                        <Link
                          to={`/billings/add/${site.id}`}
                          className="flex gap-1 items-center justify-center text-xs bg-green-100 rounded-full text-green-600 hover:text-green-800 px-2 py-0.5 hover:bg-green-50 transition-colors"
                          title="Add billing"
                        >
                          <FaCreditCard /> Add Billing
                        </Link>
                      )} */}
                      {canAssignReportMaker(site) && (
                        <button
                          onClick={() => handleAssignment("reportMaker", site)}
                          className="text-purple-600 hover:text-purple-800 p-2 hover:bg-purple-50 rounded transition-colors"
                          title="Assign Report Maker"
                          disabled={actionLoading}
                        >
                          <FaFileAlt className="w-4 h-4" />
                        </button>
                      )}

                      {canAssignValidator(site) && (
                        <button
                          onClick={() => handleAssignment("validator", site)}
                          className="text-indigo-600 hover:text-indigo-800 p-2 hover:bg-indigo-50 rounded transition-colors"
                          title="Assign Validator"
                          disabled={actionLoading}
                        >
                          <FaCheckCircle className="w-4 h-4" />
                        </button>
                      )}

                      {canHoldSite(site) && site.status !== "cancelled" && site.status !== "hold" && (
                        <button
                          onClick={() => handleAction("hold", site)}
                          className="text-amber-600 hover:text-amber-800 p-2 hover:bg-amber-50 rounded transition-colors"
                          title="Put on Hold"
                          disabled={actionLoading}
                        >
                          <FaPause className="w-4 h-4" />
                        </button>
                      )}

                      {site.status === "hold" && (
                        <button
                          onClick={() => handleAction("resume", site)}
                          className="text-green-600 hover:text-green-800 p-2 hover:bg-green-50 rounded transition-colors"
                          title="Resume Site"
                          disabled={actionLoading}
                        >
                          <FaPlay className="w-4 h-4" />
                        </button>
                      )}

                      {canCancelSite(site) && site.status !== "cancelled" && (
                        <button
                          onClick={() => handleAction("cancel", site)}
                          className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded transition-colors"
                          title="Cancel Site"
                          disabled={actionLoading}
                        >
                          <FaTimes className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>

                  {!isReportMaker && (
                    <>
                  {/* Site ID */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex flex-col justify-center items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900">{site.site_id}</span>
                    </div>
                  </td>

                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="ml-2">
                      {getStatusBadge(site.status)}
                    </span>
                  </td>

                  {/* Project Name */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-900">
                        {site.project?.project_name || <span className="text-gray-400 italic">No Project</span>}
                      </span>
                    </div>
                  </td>

                  {/* Status */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {getStatusBadge(site.survey_workflow_status)}
                      {canChangeWorkflowStage && (
                        <button
                          onClick={() =>
                            setStageModal({
                              isOpen: true,
                              site,
                              toStatus: site.survey_workflow_status,
                              remarks: "",
                              saving: false,
                            })
                          }
                          className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded hover:bg-purple-100 transition-all duration-200"
                          title="Change workflow stage manually"
                        >
                          <FiSettings className="w-3 h-3" />
                          <span>Change</span>
                        </button>
                      )}
                    </div>
                  </td>


                  {/* Client */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-900">
                        {site.project?.client?.client_short_name || <span className="text-gray-400 italic">N/A</span>}
                      </span>
                    </div>
                  </td>

                  {/* Activity */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-900">
                        {site.activity?.activity_name || <span className="text-gray-400 italic">N/A</span>}
                      </span>
                    </div>
                  </td>

                  {/* Team Coordinator */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-900">
                        {site.team_coordinator ? (
                          <span>{`${site.team_coordinator.first_name} ${site.team_coordinator.last_name}`}</span>
                        ) : (
                          <span className="text-gray-400 italic">Not Assigned</span>
                        )}
                      </span>
                    </div>
                  </td>

                  {/* Site Engineer */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-900">
                        {site.site_engineer ? (
                          <div className="flex items-center gap-2">
                            {canReassignEngineer(site) && (
                              <button
                                onClick={() => handleAssignment("engineer", site)}
                                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-sky-700 bg-sky-50 border border-sky-200 rounded hover:bg-sky-100 transition-all duration-200"
                                title="Reassign Engineer"
                              >
                                <FaUser className="w-3 h-3" />
                                <span>Reassign</span>
                              </button>
                            )}
                            <span>{`${site.site_engineer.first_name} ${site.site_engineer.last_name}`}</span>
                          </div>
                        ) : canAssignEngineer(site) ? (
                          <button
                            onClick={() => handleAssignment("engineer", site)}
                            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 hover:text-blue-800 transition-all duration-200 group"
                            title="Assign Engineer"
                          >
                            <FaUserPlus className="w-3.5 h-3.5 text-blue-500 group-hover:text-blue-600" />
                            <span>Assign</span>
                          </button>
                        ) : (
                          <span className="text-gray-400 italic">Not Assigned</span>
                        )}
                      </span>
                    </div>
                  </td>

                  {/* Technical Assistant */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-900">
                        {site.ta ? (
                          <div className="flex items-center gap-2">
                            {canReassignTA(site) && (
                              <button
                                onClick={() => handleAssignment("ta", site)}
                                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded hover:bg-orange-100 transition-all duration-200"
                                title="Reassign TA"
                              >
                                <FaUser className="w-3 h-3" />
                                <span>Reassign</span>
                              </button>
                            )}
                            <span>{`${site.ta.first_name} ${site.ta.last_name}`}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400 italic">Not Assigned</span>
                        )}
                      </span>
                    </div>
                  </td>

                  {/* MIS */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-900">
                        {getMisDisplayName(site) ? (
                          <div className="flex items-center gap-2">
                            {canReassignMIS(site) && (
                              <button
                                onClick={() => handleAssignment("mis", site)}
                                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-violet-700 bg-violet-50 border border-violet-200 rounded hover:bg-violet-100 transition-all duration-200"
                                title="Reassign MIS"
                              >
                                <FaUser className="w-3 h-3" />
                                <span>Reassign</span>
                              </button>
                            )}
                            <span>{getMisDisplayName(site)}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400 italic">Not Assigned</span>
                        )}
                      </span>
                    </div>
                  </td>

                  {/* Report Maker */}
                  {!isSiteEngineer && (<td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-900">
                        {site.report_designer ? (
                          <div className="flex items-center gap-2">
                            {canReassignReportMaker(site) && (
                              <button
                                onClick={() => handleAssignment("reportMaker", site)}
                                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded hover:bg-indigo-100 transition-all duration-200"
                                title="Reassign Report Maker"
                              >
                                <FaUser className="w-3 h-3" />
                                <span>Reassign</span>
                              </button>
                            )}
                            <span>{`${site.report_designer.first_name} ${site.report_designer.last_name}`}</span>
                          </div>
                        ) : canAssignReportMaker(site) ? (
                          <button
                            onClick={() => handleAssignment("reportMaker", site)}
                            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded hover:bg-indigo-100 hover:text-indigo-800 transition-all duration-200 group"
                            title="Assign Report Maker"
                          >
                            <FaUserPlus className="w-3.5 h-3.5 text-indigo-500 group-hover:text-indigo-600" />
                            <span>Assign</span>
                          </button>
                        ) : (
                          <span className="text-gray-400 italic">Not Assigned</span>
                        )}
                      </span>
                    </div>
                  </td>)}

                  {/* Validator */}
                  {!isSiteEngineer && (<td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-900">
                        {site.validator ? (
                          <div className="flex items-center gap-2">
                            {canReassignValidator(site) && (
                              <button
                                onClick={() => handleAssignment("validator", site)}
                                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded hover:bg-emerald-100 transition-all duration-200"
                                title="Reassign Validator"
                              >
                                <FaUser className="w-3 h-3" />
                                <span>Reassign</span>
                              </button>
                            )}
                            <span>{`${site.validator.first_name} ${site.validator.last_name}`}</span>
                          </div>
                        ) : canAssignValidator(site) ? (
                          <button
                            onClick={() => handleAssignment("validator", site)}
                            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 hover:text-blue-800 transition-all duration-200 group"
                            title="Assign Validator"
                          >
                            <FaUserPlus className="w-3.5 h-3.5 text-blue-500 group-hover:text-blue-600" />
                            <span>Assign</span>
                          </button>
                        ) : (
                          <span className="text-gray-400 italic">Not Assigned</span>
                        )}
                      </span>
                    </div>
                  </td>)}

                  {/* Site Priority */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900 capitalize">
                      {site.site_priority || <span className="text-gray-400 italic">N/A</span>}
                    </span>
                  </td>

                  {/* Visit Remarks */}
                  <td className="px-4 py-4">
                    <span className="text-sm text-gray-600">
                      {site.visit_remarks ? (
                        <span title={site.visit_remarks} className="truncate max-w-[150px] block">
                          {site.visit_remarks}
                        </span>
                      ) : (
                        <span className="text-gray-400 italic">No remarks</span>
                      )}
                    </span>
                  </td>

                  {/* Site Creation Date */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">{formatDate(site.created_at)}</span>
                    </div>
                  </td>

                  {/* SE Assign Date */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-600">
                      {site.site_engineer_assign_date ? formatDate(site.site_engineer_assign_date) : <span className="text-gray-400 italic">N/A</span>}
                    </span>
                  </td>

                  {/* Field Work Start Date */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">
                        {site.field_work_started_date || site.site_unlock_time
                          ? formatDate(site.field_work_started_date || site.site_unlock_time || "")
                          : <span className="text-gray-400 italic">N/A</span>}
                      </span>
                    </div>
                  </td>

                  {/* First Data Upload Date */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">
                        {site.first_data_upload_date ? formatDate(site.first_data_upload_date) : <span className="text-gray-400 italic">N/A</span>}
                      </span>
                    </div>
                  </td>

                  {/* Field Work Complete Date */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">
                        {site.field_work_completed_date ? formatDate(site.field_work_completed_date) : <span className="text-gray-400 italic">N/A</span>}
                      </span>
                    </div>
                  </td>

                  {/* TA Review Date */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-600">
                      {site.ta_work_date ? formatDate(site.ta_work_date) : <span className="text-gray-400 italic">N/A</span>}
                    </span>
                  </td>

                  {/* Report Prepare Date */}
                  {!isSiteEngineer && (
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-600">
                      {site.report_designer_work_date ? formatDate(site.report_designer_work_date) : <span className="text-gray-400 italic">N/A</span>}
                    </span>
                  </td>
                  )}

                  {/* Report Finalize Date */}
                  {!isSiteEngineer && (
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-600">
                      {site.report_finalized_date || site.validator_work_date ? formatDate(site.report_finalized_date || site.validator_work_date || "") : <span className="text-gray-400 italic">N/A</span>}
                    </span>
                  </td>
                  )}

                  {/* Report Sent to Client Date */}
                  {!isSiteEngineer && (
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-600">
                      {site.report_sent_to_client_date ? formatDate(site.report_sent_to_client_date) : <span className="text-gray-400 italic">N/A</span>}
                    </span>
                  </td>
                  )}

                  {/* Updated At */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">{formatDate(site.updated_at)}</span>
                    </div>
                  </td>
                    </>
                  )}

                  {isReportMaker && (
                    <>
                      {/* site_id */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm font-semibold text-gray-900">{site.site_id || <span className="text-gray-400 italic">N/A</span>}</span>
                      </td>

                      {/* project_short_name */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-600">
                          {site.project?.project_short_name || <span className="text-gray-400 italic">N/A</span>}
                        </span>
                      </td>

                      {/* activity_name */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-600">
                          {site.project?.activity?.activity_name || site.activity?.activity_name || <span className="text-gray-400 italic">N/A</span>}
                        </span>
                      </td>

                      {/* site_name */}
                      <td className="px-4 py-4">
                        <span className="text-sm text-gray-600">
                          {site.site_name ? (
                            <span title={site.site_name} className="truncate max-w-[200px] block">
                              {site.site_name}
                            </span>
                          ) : (
                            <span className="text-gray-400 italic">N/A</span>
                          )}
                        </span>
                      </td>

                      {/* site_address */}
                      <td className="px-4 py-4">
                        <span className="text-sm text-gray-600">
                          {site.site_address ? (
                            <span title={site.site_address} className="truncate max-w-[250px] block">
                              {site.site_address}
                            </span>
                          ) : (
                            <span className="text-gray-400 italic">N/A</span>
                          )}
                        </span>
                      </td>

                      {/* opco_name */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-600">
                          {site.opco_name || <span className="text-gray-400 italic">N/A</span>}
                        </span>
                      </td>

                      {/* opco_id */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-600">
                          {site.opco_id || <span className="text-gray-400 italic">N/A</span>}
                        </span>
                      </td>

                      {/* state_name */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-600">
                          {site.state_circle?.state_name || <span className="text-gray-400 italic">N/A</span>}
                        </span>
                      </td>

                      {/* pincode */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-600">
                          {site.pincode || <span className="text-gray-400 italic">N/A</span>}
                        </span>
                      </td>

                      {/* site_latitude */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-600">
                          {site.site_latitude || <span className="text-gray-400 italic">N/A</span>}
                        </span>
                      </td>

                      {/* site_longitude */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-600">
                          {site.site_longitude || <span className="text-gray-400 italic">N/A</span>}
                        </span>
                      </td>

                      {/* site_priority */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900 capitalize">
                          {site.site_priority || <span className="text-gray-400 italic">N/A</span>}
                        </span>
                      </td>

                      {/* customer_requirement */}
                      <td className="px-4 py-4">
                        <span className="text-sm text-gray-600">
                          {site.customer_requirement ? (
                            <span title={site.customer_requirement} className="truncate max-w-[200px] block">
                              {site.customer_requirement}
                            </span>
                          ) : (
                            <span className="text-gray-400 italic">N/A</span>
                          )}
                        </span>
                      </td>

                      {/* site_information1 */}
                      <td className="px-4 py-4">
                        <span className="text-sm text-gray-600">
                          {site.site_information1 ? (
                            <span title={site.site_information1} className="truncate max-w-[200px] block">
                              {site.site_information1}
                            </span>
                          ) : (
                            <span className="text-gray-400 italic">N/A</span>
                          )}
                        </span>
                      </td>

                      {/* site_information2 */}
                      <td className="px-4 py-4">
                        <span className="text-sm text-gray-600">
                          {site.site_information2 ? (
                            <span title={site.site_information2} className="truncate max-w-[200px] block">
                              {site.site_information2}
                            </span>
                          ) : (
                            <span className="text-gray-400 italic">N/A</span>
                          )}
                        </span>
                      </td>

                      {/* site_information3 */}
                      <td className="px-4 py-4">
                        <span className="text-sm text-gray-600">
                          {site.site_information3 ? (
                            <span title={site.site_information3} className="truncate max-w-[200px] block">
                              {site.site_information3}
                            </span>
                          ) : (
                            <span className="text-gray-400 italic">N/A</span>
                          )}
                        </span>
                      </td>

                      {/* mis */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">
                          {getMisDisplayName(site) || <span className="text-gray-400 italic">N/A</span>}
                        </span>
                      </td>

                      {/* report_maker */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">
                          {site.report_designer ? `${site.report_designer.first_name} ${site.report_designer.last_name}` : <span className="text-gray-400 italic">N/A</span>}
                        </span>
                      </td>

                      {/* report_completed_date */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-600">
                          {(site.workflow_status_dates?.report_completed || site.report_designer_work_date) ? formatDate((site.workflow_status_dates?.report_completed || site.report_designer_work_date) as string) : <span className="text-gray-400 italic">N/A</span>}
                        </span>
                      </td>

                      {/* status */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="ml-2">
                          {getStatusBadge(site.status)}
                        </span>
                      </td>

                      {/* survey_workflow_status */}
                      <td className="px-4 py-4 whitespace-nowrap">{getStatusBadge(site.survey_workflow_status)}</td>
                    </>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Pagination pagination={pagination} onPageChange={onPageChange} onLimitChange={onLimitChange} />

      {/* Assignment Modal */}
      {assignmentModal.isOpen && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
            onClick={() => setAssignmentModal({ isOpen: false, type: null, site: null })}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded shadow-xl max-w-md w-full">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {(() => {
                    const hasExisting = assignmentModal.type === "engineer" ? assignmentModal.site?.site_engineer :
                      assignmentModal.type === "ta" ? assignmentModal.site?.ta :
                        assignmentModal.type === "mis" ? assignmentModal.site?.mis :
                          assignmentModal.type === "reportMaker" ? assignmentModal.site?.report_designer :
                            assignmentModal.site?.validator;
                    return hasExisting ? "Reassign" : "Assign";
                  })()}{" "}
                  {assignmentModal.type === "engineer"
                    ? "Engineer"
                    : assignmentModal.type === "ta"
                      ? "Technical Associate"
                      : assignmentModal.type === "mis"
                        ? "MIS"
                        : assignmentModal.type === "reportMaker"
                          ? "Report Maker"
                          : "Validator"}
                </h3>
                <div className="space-y-4">
                  <SelectWithSearch2
                    placeholder={`Select ${assignmentModal.type === "engineer" ? "Engineer" :
                      assignmentModal.type === "ta" ? "Technical Associate" :
                        assignmentModal.type === "mis" ? "MIS" :
                          assignmentModal.type === "reportMaker" ? "Report Maker" : "Validator"
                      }`}
                    value={null}
                    onChange={(option) => option && executeAssignment({ user: option })}
                    apiEndpoint="/survey/sites/assignment/users"
                    extraParams={{
                      role_id: assignmentModal.type === "engineer" ? SITE_ENGINEER_ASSIGNABLE_ROLE_IDS :
                        assignmentModal.type === "ta" ? [5] :
                          assignmentModal.type === "mis" ? [6] :
                            assignmentModal.type === "reportMaker" ? [9] : [10, 5, 4, 6, 3],
                      project_id: assignmentModal.site?.project_id,
                      src: "all",
                      exclude_user_id: user?.id,
                    }}
                    mapOption={(user: any) => ({
                      id: user.id,
                      value: user.id,
                      label: `${user.first_name} ${user.last_name} (${user.username}) - ${user?.employee_code || 'N/A'}`,
                    })}
                    className="w-full"
                  />
                </div>
                <div className="flex justify-end mt-6">
                  <button
                    onClick={() => setAssignmentModal({ isOpen: false, type: null, site: null })}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                    disabled={actionLoading}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Manual workflow stage override */}
      {stageModal.isOpen && stageModal.site && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
            onClick={() =>
              !stageModal.saving &&
              setStageModal({ isOpen: false, site: null, toStatus: "", remarks: "", saving: false })
            }
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded shadow-xl max-w-md w-full">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900">Change Workflow Stage</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {stageModal.site.site_id}
                  {stageModal.site.site_name ? ` · ${stageModal.site.site_name}` : ""}
                </p>
                <p className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                  This moves the site straight to the stage you pick — every stage in
                  between is skipped and its work is never done. The change is recorded
                  in the site's workflow history.
                </p>

                <div className="mt-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Current Stage
                    </label>
                    <div>{getStatusBadge(stageModal.site.survey_workflow_status)}</div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      New Stage <span className="text-red-600">*</span>
                    </label>
                    <SearchableSelect
                      value={
                        ALL_WORKFLOW_STAGE_OPTIONS.find((o) => o.value === stageModal.toStatus) || null
                      }
                      onChange={(option: any) =>
                        setStageModal((prev) => ({ ...prev, toStatus: option?.value || "" }))
                      }
                      options={ALL_WORKFLOW_STAGE_OPTIONS}
                      placeholder="Select workflow stage"
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reason
                    </label>
                    <textarea
                      rows={3}
                      value={stageModal.remarks}
                      onChange={(e) =>
                        setStageModal((prev) => ({ ...prev, remarks: e.target.value }))
                      }
                      placeholder="Why is the stage being changed manually?"
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-6">
                  <button
                    onClick={() =>
                      setStageModal({ isOpen: false, site: null, toStatus: "", remarks: "", saving: false })
                    }
                    disabled={stageModal.saving}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={submitStageChange}
                    disabled={
                      stageModal.saving ||
                      !stageModal.toStatus ||
                      stageModal.toStatus === stageModal.site.survey_workflow_status
                    }
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-900 disabled:bg-blue-400 disabled:cursor-not-allowed"
                  >
                    {stageModal.saving ? "Saving..." : "Change Stage"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Action Modal (Cancel/Hold/Resume) */}
      <CancelHoldModal
        isOpen={actionModal.isOpen}
        onClose={() => setActionModal({ isOpen: false, type: null, site: null, reason: "" })}
        action={actionModal.type}
        siteId={actionModal.site?.id.toString() || ""}
        onSuccess={() => {
          onPageChange(pagination.page);
        }}
      />

      {/* Excel Download Modal */}
      <ExcelDownload
        isOpen={showExcelModal}
        onClose={() => setShowExcelModal(false)}
        data={exportData}
        filename="project_sites"
        title="Export Sites Data"
        description="Download comprehensive site details"
      />

      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        filters={filters}
        onFiltersChange={setFilters}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        projects={projects}
        activities={activities}
        siteEngineerOptions={siteEngineerOptions}
        taOptions={taOptions}
        misOptions={misOptions}
        reportMakerOptions={reportMakerOptions}
        validatorOptions={validatorOptions}
        onUserSearchChange={onUserSearchChange}
        usersLoading={filterUsersLoading}
      />
    </div>
  )
}
